import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { Numpad } from './Numpad';
import { CustomerReceipt } from './receipts/CustomerReceipt';
import { PaymentMethod } from '../types/transaction';
import { POSOrderWithItems } from '../types/admin';
import { usePOSShopSettings } from '../hooks/useShopSettings';
import {
  CheckCircle2,
  Loader2,
  X,
  Printer,
  ShoppingCart,
  Banknote,
  ArrowRightLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useReactToPrint } from 'react-to-print';

interface CheckoutModalProps {
  open: boolean;
  onClose: () => void;
  total: number;
  subtotal: number;
  vatAmount: number;
  fiscalStamp: number;
  onComplete: (paymentData: {
    payment_method: string;
    payment_amount: number;
    change_amount: number;
  }) => Promise<POSOrderWithItems>;
  onNewSale: () => void;
}

export const CheckoutModal = ({
  open,
  onClose,
  total,
  subtotal,
  vatAmount,
  fiscalStamp,
  onComplete,
  onNewSale,
}: CheckoutModalProps) => {
  const [step, setStep] = useState<'payment' | 'processing' | 'receipt'>('payment');
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>('cash');
  const [cashAmount, setCashAmount] = useState('0');
  const [isProcessing, setIsProcessing] = useState(false);
  const [completedOrder, setCompletedOrder] = useState<POSOrderWithItems | null>(null);

  const { data: settings } = usePOSShopSettings();
  const receiptRef = useRef<HTMLDivElement>(null);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount) + ' DA';
  };

  const cashValue = parseFloat(cashAmount) || 0;
  const change = selectedMethod === 'cash' ? Math.max(0, cashValue - total) : 0;
  const canPay = selectedMethod === 'cash' ? cashValue >= total : true;

  // Quick exact amount
  const handleExactAmount = () => {
    setCashAmount(total.toFixed(2));
  };

  // Handle payment confirmation
  const handleConfirmPayment = async () => {
    setIsProcessing(true);
    setStep('processing');

    try {
      const paymentAmount = selectedMethod === 'cash' ? cashValue : total;
      const changeAmount = selectedMethod === 'cash' ? Math.max(0, cashValue - total) : 0;

      const order = await onComplete({
        payment_method: selectedMethod,
        payment_amount: paymentAmount,
        change_amount: changeAmount,
      });

      setCompletedOrder(order);
      setStep('receipt');
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
      setStep('payment');
    }
  };

  // Print receipt
  const handlePrint = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: completedOrder ? `Reçu-${completedOrder.order_number}` : 'Reçu',
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      @media print {
        body {
          margin: 0;
          padding: 0;
        }
      }
    `,
  });

  // Handle new sale
  const handleNewSale = () => {
    handleReset();
    onNewSale();
    onClose();
  };

  const handleReset = () => {
    setStep('payment');
    setSelectedMethod('cash');
    setCashAmount('0');
    setIsProcessing(false);
    setCompletedOrder(null);
  };

  const handleClose = () => {
    if (step === 'receipt') {
      // If on receipt, treat as new sale
      handleNewSale();
    } else if (step !== 'processing') {
      handleReset();
      onClose();
    }
  };

  // Processing view
  if (step === 'processing') {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-16 h-16 text-emerald-600 animate-spin mb-4" />
            <p className="text-lg font-medium">
              معالجة الدفع / Traitement du paiement...
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Receipt view
  if (step === 'receipt' && completedOrder) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-500" />
              <span>تم الدفع بنجاح! / Paiement réussi!</span>
            </DialogTitle>
          </DialogHeader>

          {/* Change info for cash */}
          {completedOrder.payment_method === 'cash' && (completedOrder.change_amount || 0) > 0 && (
            <div className="bg-blue-50 dark:bg-blue-950/30 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-4 mx-1">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ArrowRightLeft className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900 dark:text-blue-200">
                    الباقي / Monnaie à rendre:
                  </span>
                </div>
                <span className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {formatCurrency(completedOrder.change_amount || 0)}
                </span>
              </div>
            </div>
          )}

          {/* Receipt Preview */}
          <div className="flex-1 overflow-y-auto border border-border rounded-lg bg-white dark:bg-gray-50 max-h-[50vh]">
            <div className="transform scale-90 origin-top">
              <CustomerReceipt
                ref={receiptRef}
                order={completedOrder}
                shopName={settings?.shop_name || 'leBusiness POS'}
                shopAddress={settings?.shop_address}
                shopPhone={settings?.shop_phone}
                shopTaxId={settings?.tax_id}
              />
            </div>
          </div>

          {/* Hidden full-size receipt for printing */}
          <div className="hidden">
            <CustomerReceipt
              ref={receiptRef}
              order={completedOrder}
              shopName={settings?.shop_name || 'leBusiness POS'}
              shopAddress={settings?.shop_address}
              shopPhone={settings?.shop_phone}
              shopTaxId={settings?.tax_id}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              className="flex-1 h-12 gap-2 text-base"
            >
              <Printer className="w-5 h-5" />
              طباعة / Imprimer
            </Button>
            <Button
              onClick={handleNewSale}
              className="flex-1 h-12 gap-2 text-base bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <ShoppingCart className="w-5 h-5" />
              بيع جديد / Nouvelle vente
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Payment view (main)
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="text-xl">الدفع / Paiement</span>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Order Summary */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
            <div className="space-y-2">
              <div className="flex justify-between text-sm opacity-90">
                <span>المجموع الفرعي / Sous-total</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {vatAmount > 0 && (
              <div className="flex justify-between text-sm opacity-90">
                <span>TVA</span>
                <span>{formatCurrency(vatAmount)}</span>
              </div>
              )}
              {fiscalStamp > 0 && (
                <div className="flex justify-between text-sm opacity-90">
                  <span>الطابع الجبائي / Timbre fiscal</span>
                  <span>{formatCurrency(fiscalStamp)}</span>
                </div>
              )}
              <div className="h-px bg-white/30 my-1" />
              <div className="flex justify-between">
                <span className="text-lg font-semibold">المجموع / Total</span>
                <span className="text-3xl font-bold">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selector */}
          <div>
            <p className="text-sm font-medium text-muted-foreground mb-3">
              طريقة الدفع / Méthode de paiement
            </p>
            <PaymentMethodSelector
              selectedMethod={selectedMethod}
              onSelectMethod={(method) => {
                setSelectedMethod(method);
                setCashAmount('0');
              }}
            />
          </div>

          {/* Cash Amount Entry */}
          {selectedMethod === 'cash' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-muted-foreground">
                  <Banknote className="w-4 h-4 inline mr-1" />
                  المبلغ المدفوع / Montant reçu
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExactAmount}
                  className="text-xs h-7 px-3 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  Montant exact
                </Button>
              </div>

              <Numpad
                value={cashAmount}
                onChange={setCashAmount}
                onEnter={canPay && !isProcessing ? handleConfirmPayment : undefined}
              />

              {/* Change Display */}
              {cashValue > 0 && (
                <div
                  className={cn(
                    'rounded-xl p-4 border-2 transition-all',
                    cashValue >= total
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700'
                      : 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-700'
                  )}
                >
                  {cashValue >= total ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
                        <span className="font-medium text-emerald-900 dark:text-emerald-200">
                          الباقي / Monnaie:
                        </span>
                      </div>
                      <span className="text-3xl font-bold text-emerald-700 dark:text-emerald-300">
                        {formatCurrency(change)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-red-900 dark:text-red-200">
                        ينقص / Il manque:
                      </span>
                      <span className="text-xl font-bold text-red-700 dark:text-red-300">
                        {formatCurrency(total - cashValue)}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Non-cash confirmation */}
          {selectedMethod !== 'cash' && (
            <div className="bg-muted rounded-xl p-4 text-center">
              <p className="text-muted-foreground mb-1">
                المبلغ المطلوب / Montant à débiter
              </p>
              <p className="text-3xl font-bold text-foreground">{formatCurrency(total)}</p>
            </div>
          )}
        </div>

        {/* Confirm Button */}
        <div className="pt-3 border-t border-border">
          <Button
            size="lg"
            onClick={handleConfirmPayment}
            disabled={!canPay || isProcessing}
            className={cn(
              'w-full h-16 text-xl font-bold transition-all',
              canPay
                ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                : 'bg-gray-300 cursor-not-allowed'
            )}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                معالجة / Traitement...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-6 h-6 mr-2" />
                تأكيد الدفع / Confirmer
                {selectedMethod === 'cash' && cashValue >= total && change > 0 && (
                  <span className="ml-2 text-base opacity-90">
                    (الباقي {formatCurrency(change)})
                  </span>
                )}
                <kbd className="ml-2 text-xs font-mono bg-white/20 px-2 py-0.5 rounded">Enter ↵</kbd>
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
